import { TypedDocumentNode, gql } from '@apollo/client';
import styled from 'styled-components';

import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';

import { CollectionPrediction } from 'components/card/CollectionPrediction';
import { FlexCard } from 'components/token/FlexCard';

import type { CardsShow_anyCard } from './__generated__/index.graphql';

const CardWrapper = styled(Vertical)`
  width: calc(25% - calc(3 * var(--unit) / 4));
  flex-shrink: 0;
`;

const CardsGrid = styled(Horizontal)`
  flex-wrap: wrap;
`;

const SpecialFiveCards = styled(Horizontal)`
  position: relative;
  isolation: isolate;
  height: 160px;
  width: 100%;
  > * {
    position: absolute;
    bottom: 0;
    box-shadow: 0px 0px 68.25px 0px rgba(0, 0, 0, 0.65);
  }
  > *:nth-child(1) {
    width: calc(var(--unit) * 8);
    right: 75%;
    transform: translateX(50%) rotate(-20deg);
  }
  > *:nth-child(2) {
    width: calc(var(--unit) * 8);
    left: 75%;
    transform: translateX(-50%) rotate(20deg);
  }
  > *:nth-child(3) {
    width: calc(var(--unit) * 11);
    right: 64%;
    transform: translateX(50%) rotate(-10deg);
  }
  > *:nth-child(4) {
    width: calc(var(--unit) * 11);
    transform: translateX(-50%) rotate(6deg);
    left: 64%;
  }
  > *:nth-child(5) {
    top: 0;
    left: 50%;
    width: calc(var(--unit) * 13);
    transform: translateX(-50%);
  }
`;

type Props = {
  cards: CardsShow_anyCard[];
  specialFive?: boolean;
};

export const CardsShow = ({ cards, specialFive }: Props) => {
  if (cards.length === 5 && specialFive)
    return (
      <SpecialFiveCards>
        {cards.map(card => (
          <CardWrapper key={card.slug}>
            <FlexCard withLink={false} card={card} width={160} />
          </CardWrapper>
        ))}
      </SpecialFiveCards>
    );
  return (
    <CardsGrid center={cards.length < 4}>
      {cards.map(card => (
        <CardWrapper key={card.slug}>
          <FlexCard withLink={false} card={card} width={160} />
          <CollectionPrediction card={card} />
        </CardWrapper>
      ))}
    </CardsGrid>
  );
};

CardsShow.fragments = {
  anyCard: gql`
    fragment CardsShow_anyCard on AnyCardInterface {
      slug
      ...FlexCard_anyCard
      ...CollectionPrediction_anyCard
    }
    ${FlexCard.fragments.anyCard}
    ${CollectionPrediction.fragments.anyCard}
  ` as TypedDocumentNode<CardsShow_anyCard>,
};
